import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { AppUpdateService } from '../services/app-update.service';

/**
 * Turns "the deployment moved on under a tab that stayed open" into the normal update banner.
 *
 * Every build emits content-hashed bundles, so a deployment replaces the lazy chunk files. A tab
 * loaded before that deployment still holds the OLD chunk names, and the first navigation into a
 * feature module it has not loaded yet fails:
 *
 *   error loading dynamically imported module: https://…/slides.module-BCFOUCBJ.js
 *
 * Without this handler nothing happens at all — the click is swallowed, the route never activates
 * and the app looks broken until the user reloads by hand. Here the failure is recognised for what
 * it is and {@link AppUpdateService.reportStaleBundle} raises the existing "a new version is
 * available" banner, so one click puts the user on the current build.
 *
 * Deliberately no automatic reload: a reload would discard unsaved work on the page the user is
 * still standing on (e.g. an open slide editor), and the failed navigation itself changed nothing.
 */
@Injectable()
export class StaleBundleErrorHandler extends ErrorHandler {
  /** Browser-specific wordings for a module/chunk that could not be fetched or was served as HTML. */
  private static readonly StaleChunkPatterns = [
    /error loading dynamically imported module/i, // Firefox
    /failed to fetch dynamically imported module/i, // Chrome / Edge
    /importing a module script failed/i, // Safari
    /chunkloaderror|loading chunk \S+ failed/i, // webpack-era wording, still emitted by tooling
    /disallowed mime type|is not a valid javascript mime type/i, // server answered with the SPA shell
  ];

  // Lazy: ErrorHandler is instantiated during bootstrap, before the service graph exists.
  private readonly injector = inject(Injector);
  private reported = false;

  override handleError(error: unknown): void {
    if (StaleBundleErrorHandler.isStaleChunkError(error)) {
      this.reportOnce();
    }

    // Always keep the original error in the console — the banner replaces the dead end, not the log.
    super.handleError(error);
  }

  private reportOnce(): void {
    if (this.reported) {
      return;
    }
    this.reported = true;

    try {
      this.injector.get(AppUpdateService).reportStaleBundle();
    } catch {
      // Nothing to recover with (injector already destroyed) — the logged error still stands.
    }
  }

  private static isStaleChunkError(error: unknown): boolean {
    const message = StaleBundleErrorHandler.messageOf(error);
    if (!message) {
      return false;
    }
    return StaleBundleErrorHandler.StaleChunkPatterns.some((pattern) =>
      pattern.test(message),
    );
  }

  /** Router navigation errors arrive wrapped, so the cause chain has to be walked too. */
  private static messageOf(error: unknown): string {
    const parts: string[] = [];
    let current: unknown = error;

    for (let depth = 0; current != null && depth < 5; depth++) {
      if (typeof current === 'string') {
        parts.push(current);
        break;
      }
      const candidate = current as { message?: unknown; name?: unknown; cause?: unknown };
      if (typeof candidate.name === 'string') {
        parts.push(candidate.name);
      }
      if (typeof candidate.message === 'string') {
        parts.push(candidate.message);
      }
      current = candidate.cause;
    }

    return parts.join(' ');
  }
}
